import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Clock3, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";

const MILANO_IMG =
  "https://images.unsplash.com/photo-1773226577998-e6e3ded8f3e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxtaWxhbiUyMHNreWxpbmUlMjBuaWdodHxlbnwwfHx8fDE3OTE3OTMwNTB8MA&ixlib=rb-4.1.0&q=85";

const inputCls =
  "w-full rounded-lg border border-white/15 bg-navy-900 px-4 py-3.5 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-gold";

const TEXT = {
  it: {
    seoTitle: "Contatti — Consulenza Riservata a Milano | Mediolanum",
    seoDesc: "Contatta Mediolanum Consulting Srl: sedi a Milano, tel. +39 0235983003, info@mediolanumconsulting.com. Richiedi un colloquio riservato.",
    eyebrow: "Contatti",
    copy: "Raccontateci l'operazione o l'esigenza che state valutando: ogni richiesta è letta direttamente dall'amministratore dello studio.",
    labels: ["Nome e Cognome *", "Azienda", "Email *", "Telefono", "Tipo di Richiesta *", "Messaggio *"],
    placeholders: ["Mario Rossi", "Rossi Industrie Srl", "mario.rossi@azienda.it", "+39 02 0000000"],
    msgPlaceholder: "Descrivete sinteticamente l'operazione o l'esigenza. Le informazioni saranno trattate con la massima riservatezza.",
    tipi: [
      { v: "operazione_straordinaria", l: "Operazione Straordinaria (M&A, fusioni, cessioni)" },
      { v: "consulenza_fiscale", l: "Consulenza Fiscale e Tributaria" },
      { v: "strategia_societaria", l: "Strategia Societaria e Governance" },
      { v: "crisi_ristrutturazione", l: "Crisi d'Impresa e Ristrutturazione" },
      { v: "altro", l: "Altra Richiesta" },
    ],
    submit: "Invia la Richiesta",
    sending: "Invio in corso…",
    ok: "Richiesta inviata",
    okDesc: "La segreteria dello studio vi ricontatterà entro un giorno lavorativo.",
    gdprPre: "Inviando il modulo acconsentite al trattamento dei dati ai sensi del Reg. UE 2016/679 (GDPR) per la gestione della richiesta.",
    gdprLink: "Leggi l'informativa privacy",
    sediLabel: "Le Sedi — Milano",
    sedeOp: "Sede Operativa",
    sedeLeg: "Sede Legale",
    orari: "Si riceve previo appuntamento",
    giorni: "Lun – Ven, 9:00 – 18:30",
    datiEyebrow: "Dati Societari",
  },
  en: {
    seoTitle: "Contact — Confidential Advisory in Milan | Mediolanum",
    seoDesc: "Contact Mediolanum Consulting Srl: offices in Milan, tel. +39 0235983003, info@mediolanumconsulting.com. Request a confidential meeting.",
    eyebrow: "Contact",
    copy: "Tell us about the transaction or the need you are considering: every enquiry is read directly by the firm's director.",
    labels: ["Full Name *", "Company", "Email *", "Phone", "Enquiry Type *", "Message *"],
    placeholders: ["John Smith", "Smith Industries Ltd", "john.smith@company.com", "+39 02 0000000"],
    msgPlaceholder: "Briefly describe the transaction or your needs. All information will be treated with the utmost confidentiality.",
    tipi: [
      { v: "operazione_straordinaria", l: "Extraordinary Transaction (M&A, mergers, disposals)" },
      { v: "consulenza_fiscale", l: "Tax and Fiscal Advisory" },
      { v: "strategia_societaria", l: "Corporate Strategy and Governance" },
      { v: "crisi_ristrutturazione", l: "Business Crisis and Restructuring" },
      { v: "altro", l: "Other Enquiry" },
    ],
    submit: "Send the Enquiry",
    sending: "Sending…",
    ok: "Enquiry sent",
    okDesc: "The firm's secretariat will contact you within one business day.",
    gdprPre: "By submitting the form you consent to the processing of your data under Regulation (EU) 2016/679 (GDPR) for handling your enquiry.",
    gdprLink: "Read the privacy policy",
    sediLabel: "Our Offices — Milan",
    sedeOp: "Operating Office",
    sedeLeg: "Registered Office",
    orari: "By appointment only",
    giorni: "Mon – Fri, 9:00 am – 6:30 pm",
    datiEyebrow: "Company Details",
  },
};

export default function Contatti() {
  const [form, setForm] = useState({
    nome: "",
    azienda: "",
    email: "",
    telefono: "",
    tipo_servizio: "operazione_straordinaria",
    messaggio: "",
  });
  const [busy, setBusy] = useState(false);
  const { lang } = useLang();
  const c = TEXT[lang];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success(c.ok, { description: c.okDesc });
      setForm({ nome: "", azienda: "", email: "", telefono: "", tipo_servizio: "operazione_straordinaria", messaggio: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="contatti-page">
      <Seo title={c.seoTitle} description={c.seoDesc} />
      <section className="navy-mesh mx-auto max-w-7xl px-5 pb-16 pt-44 sm:px-8">
        <SectionHeading
          level={1}
          eyebrow={c.eyebrow}
          title={lang === "en"
            ? <>A confidential meeting, <span className="text-gold-gradient italic">with no obligation.</span></>
            : <>Un colloquio riservato, <span className="text-gold-gradient italic">senza impegno.</span></>}
          copy={c.copy}
        />
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="card-surface rounded-2xl p-8 sm:p-10" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.labels[0]}</label>
                  <input required value={form.nome} onChange={set("nome")} className={inputCls} placeholder={c.placeholders[0]} data-testid="contact-nome-input" />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.labels[1]}</label>
                  <input value={form.azienda} onChange={set("azienda")} className={inputCls} placeholder={c.placeholders[1]} data-testid="contact-azienda-input" />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.labels[2]}</label>
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={c.placeholders[2]} data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.labels[3]}</label>
                  <input value={form.telefono} onChange={set("telefono")} className={inputCls} placeholder={c.placeholders[3]} data-testid="contact-telefono-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.labels[4]}</label>
                  <select value={form.tipo_servizio} onChange={set("tipo_servizio")} className={inputCls} data-testid="contact-tipo-select">
                    {c.tipi.map((tp) => (
                      <option key={tp.v} value={tp.v}>{tp.l}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.labels[5]}</label>
                  <textarea
                    required
                    rows={5}
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    className={inputCls}
                    placeholder={c.msgPlaceholder}
                    data-testid="contact-messaggio-textarea"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-form-submit"
                className="mt-8 flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-light disabled:opacity-50"
              >
                {busy ? c.sending : c.submit} <Send size={15} />
              </button>
              <p className="mt-4 text-xs font-light text-slate-500">
                {c.gdprPre}{" "}
                <Link to="/privacy" className="text-gold-light underline decoration-gold/40 underline-offset-2 transition-colors hover:text-gold" data-testid="contact-privacy-link">
                  {c.gdprLink}
                </Link>.
              </p>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.12}>
            <div className="flex h-full flex-col gap-6">
              <div className="card-surface overflow-hidden rounded-2xl" data-testid="contact-map-card">
                <div className="relative h-52 overflow-hidden">
                  <img src={MILANO_IMG} alt="Milano" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/30 to-transparent" />
                  <div className="absolute bottom-4 left-5 flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold text-navy-950">
                      <MapPin size={15} />
                    </span>
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold-light">{c.sediLabel}</p>
                  </div>
                </div>
                <div className="space-y-5 p-7">
                  <div className="flex gap-3">
                    <MapPin size={17} className="mt-0.5 shrink-0 text-gold" />
                    <p className="text-sm font-light leading-relaxed text-slate-300" data-testid="contact-address-operativa">
                      <span className="font-medium text-slate-200">{c.sedeOp}</span><br />
                      Via Panfilo Castaldi 8<br />20124 Milano (MI)
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Phone size={17} className="mt-0.5 shrink-0 text-gold" />
                    <p className="text-sm font-light text-slate-300">
                      <a href="tel:+390235983003" className="text-gold-light transition-colors hover:text-gold" data-testid="contact-phone">
                        Tel. +39 0235983003
                      </a>
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <MapPin size={17} className="mt-0.5 shrink-0 text-gold" />
                    <p className="text-sm font-light leading-relaxed text-slate-300" data-testid="contact-address">
                      <span className="font-medium text-slate-200">{c.sedeLeg}</span><br />
                      Via Card. Branda Castiglioni 2/1<br />20156 Milano (MI)
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Mail size={17} className="mt-0.5 shrink-0 text-gold" />
                    <p className="text-sm font-light text-slate-300">
                      <a href="mailto:info@mediolanumconsulting.com" className="text-gold-light transition-colors hover:text-gold" data-testid="contact-email-link">
                        info@mediolanumconsulting.com
                      </a>
                      <br />
                      PEC: <a href="mailto:mediocons@pec.it" className="text-gold-light transition-colors hover:text-gold" data-testid="contact-pec">mediocons@pec.it</a>
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Clock3 size={17} className="mt-0.5 shrink-0 text-gold" />
                    <p className="text-sm font-light text-slate-300">{c.orari}<br />{c.giorni}</p>
                  </div>
                </div>
              </div>

              <div className="card-surface rounded-2xl p-7" data-testid="contact-legal-card">
                <p className="eyebrow">{c.datiEyebrow}</p>
                <p className="mt-4 text-sm font-light leading-relaxed text-slate-400">
                  Mediolanum Consulting Srl<br />
                  P.IVA / C.F. 14419200960<br />
                  REA MI-2781751 · Cap. Soc. € 10.000,00<br />
                  Codice ATECO 70.20.09
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
